import { FaUser, FaLock, FaEye, FaEyeSlash } from "react-icons/fa";

import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";

import {
  finishPasskeyLogin,
  finishTotpLogin,
  loginRequest,
  takePasskeyUiError,
} from "../../api/auth";
import { useAuth } from "../../context/AuthContext";
import {
  homePath,
  normalizeAlias,
  persistLastAlias,
} from "../../utils/contractPath";
import { normalizeNmLogin } from "../../utils/normalizeNmLogin";
import { EnvBadge } from "../shared/EnvBadge/EnvBadge";
import { TotpChallengeForm } from "../shared/TotpChallenge/TotpChallengeForm";
import "./Login.css";

const PWD_CHANGE_TOKEN_KEY = "spm_password_change_token";

export function persistPasswordChangeToken(token) {
  try {
    if (token) sessionStorage.setItem(PWD_CHANGE_TOKEN_KEY, token);
    else sessionStorage.removeItem(PWD_CHANGE_TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

export function readPasswordChangeToken() {
  try {
    return sessionStorage.getItem(PWD_CHANGE_TOKEN_KEY) || "";
  } catch {
    return "";
  }
}

export function clearPasswordChangeToken() {
  persistPasswordChangeToken("");
}

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { cdAlias } = useParams();
  const { login, isAuthenticated, user } = useAuth();
  const [nmLogin, setNmLogin] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState("");
  const [totpUi, setTotpUi] = useState(null);
  const [totpCode, setTotpCode] = useState("");
  const [totpResolve, setTotpResolve] = useState(null);
  const alias = normalizeAlias(cdAlias);

  useEffect(() => {
    if (location.state?.info) {
      setInfo(String(location.state.info));
    }
  }, [location.state]);

  useEffect(() => {
    const show = (msg) => {
      if (msg) setError(msg);
    };
    show(takePasskeyUiError());
    const onErr = (event) => show(event.detail);
    window.addEventListener("spm-passkey-ui-error", onErr);
    return () => window.removeEventListener("spm-passkey-ui-error", onErr);
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      const a = normalizeAlias(user?.cd_alias) || alias;
      navigate(homePath(a), { replace: true });
    }
  }, [isAuthenticated, navigate, user?.cd_alias, alias]);

  function askTotpCode(state) {
    // handleSubmit ficou com loading=true; libertar o botão enquanto o utilizador
    // introduz o código TOTP.
    setLoading(false);
    setTotpUi(state);
    setTotpCode("");
    setInfo(
      state.mode === "enroll"
        ? "Leia o QR (ou introduza a chave) na app autenticadora e confirme o código."
        : "Introduza o código de 6 dígitos da app autenticadora."
    );
    return new Promise((resolve, reject) => {
      setTotpResolve({ resolve, reject });
    });
  }

  function submitTotpCode(event) {
    event.preventDefault();
    const code = String(totpCode || "").trim();
    if (!/^\d{6}$/.test(code)) {
      setError("Introduza o código de 6 dígitos.");
      return;
    }
    setError(null);
    if (totpResolve?.resolve) {
      setLoading(true);
      const resolve = totpResolve.resolve;
      setTotpResolve(null);
      // Manter ecrã TOTP visível com «A verificar…» até o login concluir.
      resolve(code);
    }
  }

  function cancelTotp() {
    if (totpResolve?.reject) {
      totpResolve.reject(new Error("Código TOTP cancelado."));
    }
    setTotpResolve(null);
    setTotpUi(null);
    setTotpCode("");
    setLoading(false);
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setInfo("");
    if (!alias) {
      setError(
        "Acesse o login pela URL do contrato (ex.: /gfandfsolution)."
      );
      return;
    }
    setLoading(true);
    try {
      const data = await loginRequest(
        normalizeNmLogin(nmLogin),
        password,
        alias
      );
      const resolvedAlias = normalizeAlias(data.cd_alias || alias);
      persistLastAlias(resolvedAlias);

      if (data.must_change_password) {
        persistPasswordChangeToken(data.password_change_token || "");
        navigate(`/${resolvedAlias}/redefinir-senha`, {
          replace: true,
          state: {
            nm_login: data.nm_login || normalizeNmLogin(nmLogin),
            reason: "must_change",
          },
        });
        return;
      }

      let session = data;
      if (data.auth_step === "enroll" || data.auth_step === "passkey") {
        setInfo(
          data.auth_step === "enroll"
            ? "Confirme a passkey neste aparelho para concluir o primeiro acesso."
            : "Confirme a passkey neste aparelho."
        );
        session = await finishPasskeyLogin(
          data.webauthn_pending_token,
          data.auth_step
        );
      } else if (
        data.auth_step === "enroll_totp" ||
        data.auth_step === "totp"
      ) {
        session = await finishTotpLogin(
          data.totp_pending_token,
          data.auth_step,
          askTotpCode
        );
      }

      login(session.access_token, {
        ...(session.user || data.user),
        cd_alias: resolvedAlias,
        active_company_id:
          session.active_company_id !== undefined ? session.active_company_id : null,
        primary_company_id:
          session.primary_company_id !== undefined
            ? session.primary_company_id
            : null,
        permitted_companies: session.permitted_companies ?? [],
        enabled_modules: session.enabled_modules ?? [],
        enabled_submodules: session.enabled_submodules ?? [],
        authorizations: session.authorizations ?? [],
      });
      // Hard navigation: remonta shell/nav com cookie de sessão já gravado.
      window.location.assign(homePath(resolvedAlias));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erro ao entrar.");
      setTotpUi(null);
      setTotpResolve(null);
    } finally {
      setLoading(false);
    }
  };

  if (totpUi) {
    return (
      <div className="container">
        <TotpChallengeForm
          idPrefix="login-totp"
          mode={totpUi.mode}
          secret={totpUi.secret || ""}
          qrDataUrl={totpUi.qr_data_url || ""}
          otpauthUri={totpUi.otpauth_uri || ""}
          alias={alias || ""}
          info={info}
          error={error || ""}
          code={totpCode}
          onCodeChange={setTotpCode}
          onSubmit={submitTotpCode}
          onCancel={cancelTotp}
          loading={loading}
          showEnvBadge
        />
      </div>
    );
  }

  return (
    <div className="container">
      <form onSubmit={handleSubmit} noValidate>
        <h1>Kapital Map</h1>
        <p className="login-subtitle">
          Plataforma de Gestão Patrimonial e Financeira
        </p>
        <div className="login-env-row">
          <EnvBadge />
        </div>
        {alias ? (
          <p className="login-contract-badge" role="status">
            Contrato: <strong>{alias}</strong>
          </p>
        ) : (
          <p className="login-feedback login-feedback--error" role="alert">
            Use a URL do contrato para entrar (ex.:{" "}
            <strong>/gfandfsolution</strong>).
          </p>
        )}
        {info ? (
          <p className="login-feedback login-feedback--ok" role="status">
            {info}
          </p>
        ) : null}
        {error ? (
          <p className="login-feedback login-feedback--error" role="alert">
            {error}
          </p>
        ) : null}
        <div className="input-group">
          <input
            type="text"
            name="nm_login"
            autoComplete="username"
            placeholder="E-mail ou Usuário"
            required
            disabled={loading || !alias}
            value={nmLogin}
            onChange={(e) => setNmLogin(normalizeNmLogin(e.target.value))}
          />
          <FaUser className="icon" />
        </div>
        <div className="input-group">
          <input
            type={showPassword ? "text" : "password"}
            name="password"
            className="has-toggle"
            autoComplete="current-password"
            placeholder="Senha"
            required
            disabled={loading || !alias}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <FaLock className="icon" />
          <button
            type="button"
            className="icon-toggle"
            tabIndex={0}
            aria-label={showPassword ? "Ocultar senha" : "Exibir senha"}
            disabled={loading || !alias}
            onClick={() => setShowPassword((v) => !v)}
          >
            {showPassword ? <FaEyeSlash aria-hidden /> : <FaEye aria-hidden />}
          </button>
        </div>

        <div className="recall-forget">
          <button
            type="button"
            className="link-button"
            disabled={!alias || loading}
            onClick={() =>
              navigate(`/${alias}/esqueceu-senha`, {
                state: { nm_login: normalizeNmLogin(nmLogin) },
              })
            }
          >
            Esqueceu a senha?
          </button>
        </div>

        <button
          type="submit"
          className="submit-btn"
          disabled={loading || !alias}
        >
          {loading ? "Entrando…" : "Entrar"}
        </button>
      </form>
    </div>
  );
};

export default Login;
